/**
 * #280 — game-local position snapshot for the trading card.
 *
 * "What do I already hold on the market I am about to trade?", answered
 * in-place so the trader does not have to leave the ticket for the Positions
 * page. Read-only: quantity, venue-native side, and the canonical before-fees
 * average entry. No P&L, no order actions.
 *
 * Follows the panel's EFFECTIVE game (the chip-aware `game` binding), so
 * switching to a spread, total, or prop chip re-points the snapshot at that
 * market — a moneyline holding does not follow you onto the spread.
 *
 * Sits directly above `PairContextCard`, which answers a different question
 * (match-level blended pair cost and residual exposure, Kalshi-only, folded
 * from the fills ledger). This card is the per-market canonical read; the two
 * are framed differently on purpose and neither restates the other.
 *
 * The projection in `gamePositionProjection.ts` owns every accounting rule.
 * Display lives here — including the quantity contract, which is the one thing
 * the projection deliberately does not decide.
 */

import { useMemo } from 'react';
import { usePolymarketRelayPositions } from '@/hooks/usePolymarketRelayPositions';
import { useAccountStore } from '@/stores/accountStore';
import { polyPositionBetLabel } from '@/components/cards/positionCardModel';
import { fmtCents, fmtQty } from '@/components/cards/cardFormat';
import { describeMarket } from '@/lib/kalshiTicker/describeMarket';
import type { KalshiPosition } from '@/types/kalshi';
import type { UserPositionSlice } from '@/hooks/useUserKalshiPOS';
import {
  projectGamePosition,
  type GamePositionProjection,
  type GamePositionRow,
  type PositionVenue,
} from './gamePositionProjection';
import type { GameRow } from './types';

const VENUE_LABEL: Record<PositionVenue, string> = {
  kalshi: 'Kalshi',
  polymarket: 'Polymarket',
};

/**
 * Orientation comes from the ticker, via the repo's canonical helper. Defined at
 * module scope so its identity is stable across renders.
 *
 * `betTeam` is the market's named (YES) outcome regardless of the side passed —
 * the `(BAL) NO` convention — which is exactly what this card wants: a NO row
 * should name the market it settles against, not the team the trader is
 * implicitly backing. `describeMarket` reads `useMarketStore.getState()`, so a
 * label can resolve from `null` to a name once the market frame lands; the 5s
 * position poll re-runs the projection, so it converges without extra wiring.
 */
const kalshiOutcomeLabel = (ticker: string, side: 'yes' | 'no'): string | null =>
  describeMarket(ticker, side).betTeam;

/**
 * The quantity contract for this surface.
 *
 * #144's `fmtQty` is the canonical user-facing contract-count display. The
 * projection retains the exact source quantity for calculations and tests; this
 * card applies the accepted whole-number presentation only at render time.
 */
export function formatHeldQuantity(qty: number): string {
  return fmtQty(qty);
}

/** Adapt the exact Kalshi position source used by Position Tracker to the
 * ticker-keyed shape consumed by the pure game projection. The store normally
 * carries one aggregate per ticker/side. Folding defensively avoids dropping
 * exposure if a transitional payload ever contains more than one row. */
export function accountPositionsToSlices(
  positions: readonly KalshiPosition[],
  tickers: ReadonlySet<string>,
): Map<string, UserPositionSlice> {
  type SideAcc = { count: number; cost: number; basisKnown: boolean };
  const accumulators = new Map<string, { yes: SideAcc; no: SideAcc }>();

  for (const position of positions) {
    if (!tickers.has(position.ticker) || position.position <= 0) continue;
    const acc = accumulators.get(position.ticker) ?? {
      yes: { count: 0, cost: 0, basisKnown: true },
      no: { count: 0, cost: 0, basisKnown: true },
    };
    const side = acc[position.side];
    side.count += position.position;
    if (Number.isFinite(position.averagePrice) && position.averagePrice > 0) {
      side.cost += position.averagePrice * position.position;
    } else {
      side.basisKnown = false;
    }
    accumulators.set(position.ticker, acc);
  }

  const result = new Map<string, UserPositionSlice>();
  for (const [ticker, acc] of accumulators) {
    const average = (side: SideAcc): number | null =>
      side.basisKnown && side.count > 0
        ? Math.round((side.cost / side.count) * 100) / 100
        : null;
    result.set(ticker, {
      yesCount: acc.yes.count,
      noCount: acc.no.count,
      yesAvgCents: average(acc.yes),
      noAvgCents: average(acc.no),
    });
  }
  return result;
}

/** Held quantity + average, in PR #273's single-sourced wording: an absent
 *  average is spelled out, never rendered as a price.
 *
 *  `ladder/heldContractsTitle.ts` owns this convention for the Kalshi hover
 *  rings. It cannot be called here — it takes a Kalshi `UserPositionSlice` and
 *  folds BOTH sides into one string, whereas this card renders one row per side
 *  and also carries Polymarket rows, which have no slice. The wording and the
 *  0.01¢ grain are kept identical to it on purpose, and pinned by a test that
 *  compares the two outputs directly. */
function heldClause(row: GamePositionRow): string {
  const qty = formatHeldQuantity(row.quantity);
  return row.averageEntryCents == null
    ? `${qty} (avg unavailable)`
    : `${qty} @ ${fmtCents(row.averageEntryCents)} avg`;
}

/**
 * The one line the card is allowed to say about itself.
 *
 * Order matters: a broken source outranks everything, because the failure mode
 * this component exists to avoid is a blind read presented as a flat book. Only
 * once every applicable source has reported may an empty list be called "no
 * position".
 */
function snapshotNotice(projection: GamePositionProjection): string | null {
  const failed = (Object.keys(projection.sources) as PositionVenue[]).filter((v) => {
    const state = projection.sources[v];
    return state === 'error' || state === 'unavailable';
  });
  if (failed.length > 0) {
    const venues = failed.map((v) => VENUE_LABEL[v]).join(' and ');
    // Rows surviving a source failure are last-known, not current. Saying so is
    // the difference between a stale reading and a wrong one.
    return projection.stale
      ? `${venues} position data unavailable — showing the last reading.`
      : `${venues} position data unavailable.`;
  }
  if (!projection.authoritative) return 'Position data loading…';
  if (projection.rows.length === 0) return 'No position in this market.';
  return null;
}

export function GamePositionSnapshot({ game }: { game: GameRow }) {
  const kalshiTickers = useMemo(
    () =>
      [game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker].filter(
        (t): t is string => !!t,
      ),
    [game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker],
  );

  // Position Tracker's canonical Kalshi source. Reading the same store is
  // important in personal-key mode: the raw venue aggregate can differ from
  // the relay-ledger snapshot after a partial sell. A second position hook here
  // would make two dashboard surfaces disagree about both quantity and average.
  const accountPositions = useAccountStore((state) => state.positions);
  const accountStatus = useAccountStore((state) => state.status);
  const accountError = useAccountStore((state) => state.error);
  const kalshiPositionByTicker = useMemo(
    () => accountPositionsToSlices(accountPositions, new Set(kalshiTickers)),
    [accountPositions, kalshiTickers],
  );
  // Positions only — `includePnl: false` skips the /pnl round-trip entirely.
  // This card shows no P&L, so fetching it would be pure traffic.
  const poly = usePolymarketRelayPositions({ includePnl: false });

  const projection = useMemo(
    () =>
      projectGamePosition({
        game,
        kalshi: {
          positionByTicker: kalshiPositionByTicker,
          loading: accountStatus === 'connecting',
          error:
            accountStatus === 'error' || accountStatus === 'disconnected'
              ? accountError ?? 'Kalshi position data unavailable'
              : null,
          snapshotSeq: accountStatus === 'connected' ? 1 : 0,
        },
        polymarket: { positions: poly.positions, status: poly.status },
        polyOutcomeLabel: polyPositionBetLabel,
        kalshiOutcomeLabel,
      }),
    [
      game,
      kalshiPositionByTicker,
      accountStatus,
      accountError,
      poly.positions,
      poly.status,
    ],
  );

  // No venue lists a market on this game, so there is no position to report
  // either way. Rendering the header over "No position in this market." would
  // be stating a fact about a market that does not exist here.
  const applicable = (Object.keys(projection.sources) as PositionVenue[]).filter(
    (v) => projection.sources[v] !== 'absent',
  );
  if (applicable.length === 0) return null;

  const notice = snapshotNotice(projection);

  return (
    <div className="border-border border-b px-4 py-2" data-testid="game-position-snapshot">
      <div
        className="text-muted-foreground mb-1 text-[10px] font-semibold uppercase"
        title="Contracts you hold on the exact selected market, with the canonical before-fees average entry — the same basis the Positions page shows. Sides are venue-native."
      >
        Your position — this market
      </div>
      {projection.rows.map((row) => (
        <div
          key={row.key}
          data-testid="game-position-row"
          data-venue={row.venue}
          data-side={row.side}
          className="flex flex-wrap items-baseline gap-x-2 font-mono text-xs"
        >
          <span className="text-muted-foreground">
            {VENUE_LABEL[row.venue]} · {row.outcomeLabel ?? 'outcome unavailable'} ·{' '}
            {row.side.toUpperCase()}
          </span>
          <span>{heldClause(row)}</span>
        </div>
      ))}
      {notice && <div className="text-muted-foreground text-xs">{notice}</div>}
    </div>
  );
}
